import flatMap from 'lodash/flatMap';
import memoize from 'lodash/memoize';

import { Item, ItemUpdatePayload, Category } from 'apis/restaurant-api/merchant-admin/menu2/types';

import { WOLT_SPECIALS_CATEGORY_ID } from './constants';
import { flattenCategories } from '../utils';

export const buildItemUpdateDataFromItem = (item: Item, updateData: Partial<Item>): ItemUpdatePayload => ({
  id: item.id,
  option_bindings: item.option_bindings,
  version: item.version,
  alcohol_percentage: item.alcohol_percentage,
  nutrition_values: item.nutrition_values,
  nutrition_values_baseline: item.nutrition_values_baseline,
  availability_period: item.availability_period,
  baseprice: item.baseprice,
  barcode_gtin: item.barcode_gtin,
  brand_id: item.brand_id,
  caffeine_content: item.caffeine_content,
  courier_restrictions: item.courier_restrictions,
  delivery_methods: item.delivery_methods,
  deposit_permutation_id: item.deposit_permutation_id,
  dietary_preferences: item.dietary_preferences,
  discounted: item.discounted,
  display_unit: item.display_unit,
  enabled: item.enabled,
  external_id: item.external_id,
  inventory_mode: item.inventory_mode,
  is_bundle_offer: item.is_bundle_offer,
  mandatory_warnings: item.mandatory_warnings,
  more_information: item.more_information,
  merchant_sku: item.merchant_sku,
  number_of_units: item.number_of_units,
  product_hierarchy_level_id: item.product_hierarchy_level_id,
  user_requirements: item.user_requirements,
  vat_percentage: item.vat_percentage,
  vat_category_code: item.vat_category_code,
  volume_in_ml: item.volume_in_ml,
  weekly_availability: item.weekly_availability,
  weekly_visibility: item.weekly_visibility,
  weight_in_grams: item.weight_in_grams,
  source_config: item.source_config,
  max_quantity_per_purchase: item.max_quantity_per_purchase,
  min_quantity_per_purchase: item.min_quantity_per_purchase,
  additives: item.additives,
  allergens: item.allergens,
  conditions_of_use_and_storage: item.conditions_of_use_and_storage,
  country_of_origin: item.country_of_origin,
  distributor_information: item.distributor_information,
  ingredients: item.ingredients,
  nutrition_facts: item.nutrition_facts,
  producer_information: item.producer_information,
  description: item.description,
  name: item.name,
  image: item.image,
  image_blurhash: item.image_blurhash,
  images: item.images,
  forbidden_courier_capabilities: item.forbidden_courier_capabilities,
  menu_id: item.menu_id,
  required_courier_capabilities: item.required_courier_capabilities,
  sell_by_weight_config: item.sell_by_weight_config,
  external_info_hyperlink: item.external_info_hyperlink,
  restrictions: item.restrictions,
  wolt_plus_only: item.wolt_plus_only,
  return_policy: item.return_policy,
  ...updateData,
});

const buildCategoryByItemIdIndex = memoize((categories: Category[]) => {
  const categoryByItemId: { [itemId: string]: Category } = {};
  for (const category of flattenCategories(categories)) {
    for (const itemBinding of category.item_bindings) {
      categoryByItemId[itemBinding.item_id] = category;
    }
  }
  return categoryByItemId;
});
// Use WeakMap to avoid leaking memory. Overriding the cache of a memoized function instance is not documented, but it
// works with current Lodash memoize implementation. It is possible that in future this override breaks and memoize will
// resort to default cache implementation which leaks memory.
buildCategoryByItemIdIndex.cache = new WeakMap();

export const getItemCategory = (categories: Category[], itemId: string): Category | undefined => {
  const index = buildCategoryByItemIdIndex(categories);
  return index[itemId];
};

export const orderItemsByBindings = (
  items: Item[],
  categories: Category[],
  showWoltSpecials: boolean = true,
): Item[] => {
  const getItemsInCategory = (category: Category) =>
    category.item_bindings.reduce<Item[]>((categoryItems, itemBinding) => {
      const item = items.find(item => item.id === itemBinding.item_id);
      return item ? [...categoryItems, item] : categoryItems;
    }, []);

  const getItemsInCategories = (categories: Category[]) => flatMap(categories, getItemsInCategory);

  const getItemsNotBoundToCategory = () => items.filter(item => !getItemCategory(categories, item.id));

  return [
    ...getItemsInCategories(
      showWoltSpecials ? categories : categories.filter(category => category.id !== WOLT_SPECIALS_CATEGORY_ID),
    ),
    ...getItemsNotBoundToCategory(),
  ];
};
